import { useEffect, useState, useCallback, useRef } from "react";
import WindowSearch from "@/components/WindowSearch";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Copy, RefreshCw, Upload, Check, X, Pencil, BarChart3, ClipboardPaste, Tags, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import { readClipboardImageFile, imageFileFromPasteEvent, isSupportedImage } from "@/lib/clipboardImage";

const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const ASPECTS = ["1:1", "4:5", "9:16", "16:9"];

export default function InstagramTemplates() {
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const fileRef = useRef(null);
  const replaceRef = useRef(null);
  const [replaceId, setReplaceId] = useState(null);
  const [form, setForm] = useState({ name: "", aspect: "1:1", description: "", tags: "" });
  const [savingUpload, setSavingUpload] = useState(false);
  const [pastedFile, setPastedFile] = useState(null);
  const [imgPreview, setImgPreview] = useState(null);
  const [imgSource, setImgSource] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [manageCatsFor, setManageCatsFor] = useState(null); // template being category-managed
  const [catSel, setCatSel] = useState([]);
  const [savingCats, setSavingCats] = useState(false);
  const [selected, setSelected] = useState([]); // bulk-select template ids
  const [bulkCat, setBulkCat] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const applyBulk = async () => {
    if (!bulkCat || !selected.length) return;
    setBulkBusy(true);
    try {
      await api.igBulkCategorize(selected, [bulkCat]);
      const rows = await api.igAdminTemplates(active);
      setTemplates(rows);
      toast.success(`Added ${selected.length} template(s) to ${bulkCat}`);
      setSelected([]); setBulkCat("");
    } catch { toast.error("Bulk categorize failed"); } finally { setBulkBusy(false); }
  };

  const openManageCats = (t) => { setManageCatsFor(t); setCatSel(t.categories || (t.category ? [t.category] : [])); };
  const toggleCatSel = (name) => setCatSel((s) => s.includes(name) ? s.filter((x) => x !== name) : [...s, name]);
  const saveCats = async () => {
    setSavingCats(true);
    try {
      const updated = await api.igAdminSetTemplateCategories(manageCatsFor.id, catSel);
      setTemplates((x) => x.map((v) => (v.id === updated.id ? updated : v)));
      toast.success("Categories updated");
      setManageCatsFor(null);
    } catch { toast.error("Failed to update categories"); } finally { setSavingCats(false); }
  };
  const togglePublishCat = async (c) => {
    try { const u = await api.igAdminUpdateCategory(c.id, { published: !(c.published !== false) }); setCats((x) => x.map((v) => v.id === c.id ? { ...v, published: u.published } : v)); }
    catch { toast.error("Failed"); }
  };

  const setTemplateImage = (file, source) => {
    if (!file) return;
    if (!isSupportedImage(file.type)) { toast.error("Clipboard content isn't a supported image."); return; }
    setPastedFile(file); setImgSource(source);
    setImgPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(file); });
  };
  const clearTemplateImage = () => {
    setImgPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setPastedFile(null); setImgSource(null);
    if (fileRef.current) fileRef.current.value = "";
  };
  const pasteTemplateFromClipboard = async () => {
    try { const f = await readClipboardImageFile(); setTemplateImage(f, "clipboard"); }
    catch (e) { toast.error(e?.message || "Clipboard paste failed."); }
  };

  // Ctrl/Cmd+V pastes an image into the Add Template area — never intercepts plain-text paste in text fields.
  const pasteRef = useRef();
  pasteRef.current = (e) => {
    if (!uploadOpen) return;
    let file; try { file = imageFileFromPasteEvent(e); } catch (err) { toast.error(err.message); return; }
    if (file) { e.preventDefault(); setTemplateImage(file, "clipboard"); }
  };
  useEffect(() => {
    const fn = (e) => pasteRef.current && pasteRef.current(e);
    window.addEventListener("paste", fn);
    return () => window.removeEventListener("paste", fn);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.igAdminCategories(), api.igAdminStats()]);
      setCats(c); setStats(s);
      const cat = active || (c[0]?.name ?? null);
      setActive(cat);
      setTemplates(await api.igAdminTemplates(cat || undefined));
    } catch { toast.error("Could not load Instagram data."); }
    setLoading(false);
  }, [active]);

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const selectCat = async (name) => { setActive(name); setTemplates(await api.igAdminTemplates(name)); };

  const createCategory = async () => {
    if (!newCat.trim()) return;
    try { const c = await api.igAdminCreateCategory(newCat.trim()); setNewCat(""); setCats((x) => [...x, { ...c, template_count: 0 }]); toast.success("Category created"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const renameCategory = async (c) => {
    const name = window.prompt("Rename category", c.name);
    if (!name || name === c.name) return;
    try { await api.igAdminUpdateCategory(c.id, { name }); toast.success("Renamed"); if (active === c.name) setActive(name); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const deleteCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.igAdminDeleteCategory(c.id); toast.success("Deleted"); setActive(null); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doUpload = async () => {
    const file = pastedFile || fileRef.current?.files?.[0];
    if (!file) { toast.error("Choose or paste an image."); return; }
    if (!form.name.trim()) { toast.error("Template name required."); return; }
    setSavingUpload(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("name", form.name.trim()); fd.append("category", active);
      fd.append("recommended_aspect", form.aspect); fd.append("description", form.description); fd.append("tags", form.tags);
      fd.append("source", imgSource || "upload");
      await api.igAdminCreateTemplate(fd);
      toast.success("Template added");
      setUploadOpen(false); setForm({ name: "", aspect: "1:1", description: "", tags: "" });
      clearTemplateImage();
      setTemplates(await api.igAdminTemplates(active));
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setSavingUpload(false);
  };

  const patchTpl = async (id, body) => { try { const t = await api.igAdminUpdateTemplate(id, body); setTemplates((x) => x.map((v) => (v.id === id ? t : v))); } catch { toast.error("Failed"); } };
  const rename = async (t) => { const name = window.prompt("Template name", t.name); if (name && name !== t.name) patchTpl(t.id, { name }); };
  const duplicate = async (t) => { try { await api.igAdminDuplicateTemplate(t.id); toast.success("Duplicated"); setTemplates(await api.igAdminTemplates(active)); } catch { toast.error("Failed"); } };
  const del = async (t) => { if (!window.confirm(`Delete "${t.name}"?`)) return; try { await api.igAdminDeleteTemplate(t.id); setTemplates((x) => x.filter((v) => v.id !== t.id)); toast.success("Deleted"); } catch { toast.error("Failed"); } };
  const doReplace = async () => {
    const file = replaceRef.current?.files?.[0]; if (!file || !replaceId) return;
    try { const fd = new FormData(); fd.append("file", file); const t = await api.igAdminReplaceTemplate(replaceId, fd); setTemplates((x) => x.map((v) => (v.id === replaceId ? t : v))); toast.success("Replaced"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setReplaceId(null); if (replaceRef.current) replaceRef.current.value = "";
  };

  return (
    <div data-testid="instagram-templates">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-black text-white">Instagram Templates</h2>
          <p className="text-xs text-[#A1A1AA]">Manage categories and image templates for the Instagram Post Maker.</p>
        </div>
        <button onClick={refresh} disabled={loading} data-testid="ig-admin-refresh" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white disabled:opacity-50"><RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh</button>
      </div>

      {stats && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="ig-admin-stats">
          {[["Generations", stats.total], ["Successful", stats.success], ["Failed", stats.failed], ["Avg time", `${(stats.avg_ms / 1000).toFixed(1)}s`]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
              <div className="text-[11px] uppercase tracking-wide text-[#666]">{l}</div>
              <div className="mt-0.5 font-display text-xl font-black text-white">{v}</div>
            </div>
          ))}
        </div>
      )}

      <input ref={replaceRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={doReplace} />

      <div className="grid gap-5 lg:grid-cols-[240px_1fr]">
        {/* categories */}
        <div>
          <div className="mb-2 flex items-center gap-1.5">
            <input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category" data-testid="ig-new-category-input"
              className="w-full rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-xs text-white outline-none focus:border-[#E1306C]/60" />
            <button onClick={createCategory} data-testid="ig-create-category-btn" className="rounded-lg bg-[#E1306C] p-1.5 text-white"><Plus size={15} /></button>
          </div>
          <div className="space-y-1" data-testid="ig-category-list">
            {cats.map((c) => (
              <div key={c.id} className={`group flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm ${active === c.name ? "bg-[#E1306C]/15 text-white" : "text-[#A1A1AA] hover:bg-white/5"}`}>
                <button onClick={() => selectCat(c.name)} data-testid={`ig-admin-cat-${c.name}`} className="flex-1 truncate text-left font-semibold">{c.name}{c.published === false && <span className="ml-1 text-[9px] font-bold uppercase text-amber-400">· hidden</span>}</button>
                <span className="text-[10px] text-[#666]">{c.template_count}</span>
                <button onClick={() => togglePublishCat(c)} title={c.published === false ? "Publish" : "Unpublish"} data-testid={`ig-cat-publish-${c.id}`} className="opacity-0 group-hover:opacity-100 hover:text-white">{c.published === false ? <EyeOff size={12} /> : <Eye size={12} />}</button>
                <button onClick={() => renameCategory(c)} className="opacity-0 group-hover:opacity-100"><Pencil size={12} /></button>
                <button onClick={() => deleteCategory(c)} className="opacity-0 group-hover:opacity-100 hover:text-red-400"><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        </div>

        {/* templates */}
        <div>
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-bold text-white">{active || "Select a category"}</div>
            {active && <button onClick={() => setUploadOpen((v) => !v)} data-testid="ig-add-template-btn" className="flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-3 py-1.5 text-xs font-bold text-white"><Plus size={14} /> Add Template</button>}
          </div>

          {uploadOpen && active && (
            <div className="mb-4 space-y-2 rounded-xl border border-[#E1306C]/30 bg-white/[0.02] p-3" data-testid="ig-upload-form">
              <div data-testid="ig-template-image-area"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); setTemplateImage(e.dataTransfer.files?.[0], "upload"); }}
                className={`rounded-lg border border-dashed p-3 ${dragOver ? "border-[#E1306C] bg-[#E1306C]/10" : "border-white/20 bg-black/20"}`}>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" data-testid="ig-template-file" className="hidden"
                  onChange={(e) => setTemplateImage(e.target.files?.[0], "upload")} />
                {imgPreview ? (
                  <div className="flex items-center gap-3">
                    <img src={imgPreview} alt="preview" data-testid="ig-template-preview" className="h-20 w-20 rounded-lg border border-white/15 object-cover" />
                    <div className="text-[11px] text-[#A1A1AA]">Source: <span className="font-bold text-white">{imgSource === "clipboard" ? "Clipboard" : "Upload"}</span></div>
                    <div className="ml-auto flex gap-2">
                      <button type="button" onClick={() => fileRef.current?.click()} data-testid="ig-template-replace" className="rounded-lg border border-white/15 px-2.5 py-1 text-[11px] font-semibold text-white hover:border-[#E1306C]/60">Replace</button>
                      <button type="button" onClick={clearTemplateImage} data-testid="ig-template-remove" className="rounded-lg border border-white/15 px-2.5 py-1 text-[11px] font-semibold text-[#A1A1AA] hover:text-red-400">Remove</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <button type="button" onClick={() => fileRef.current?.click()} data-testid="ig-template-upload-btn" className="flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-3 py-1.5 text-xs font-bold text-white"><Upload size={13} /> Upload Template</button>
                    <button type="button" onClick={pasteTemplateFromClipboard} data-testid="ig-template-paste-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-bold text-white hover:border-[#E1306C]/60"><ClipboardPaste size={13} /> Paste From Clipboard</button>
                    <span className="text-[11px] text-[#666]">or drag &amp; drop · Ctrl / Cmd + V</span>
                  </div>
                )}
              </div>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Template name" data-testid="ig-template-name" className="w-full rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm text-white outline-none" />
              <div className="flex gap-2">
                <select value={form.aspect} onChange={(e) => setForm({ ...form, aspect: e.target.value })} data-testid="ig-template-aspect" className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm text-white outline-none">
                  {ASPECTS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
                <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="tags, comma, separated" className="flex-1 rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm text-white outline-none" />
              </div>
              <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" className="w-full rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-sm text-white outline-none" />
              <div className="flex gap-2">
                <button onClick={doUpload} disabled={savingUpload} data-testid="ig-upload-submit" className="flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">{savingUpload ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload</button>
                <button onClick={() => { setUploadOpen(false); clearTemplateImage(); }} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA]">Cancel</button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E1306C]" /></div>
          ) : templates.length === 0 ? (
            <div className="py-12 text-center text-sm text-[#666]">No templates in this category yet.</div>
          ) : (
            <>
            {selected.length > 0 && (
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#E1306C]/40 bg-[#E1306C]/10 px-3 py-2" data-testid="ig-bulk-bar">
                <span className="text-xs font-bold text-white">{selected.length} selected</span>
                <select value={bulkCat} onChange={(e) => setBulkCat(e.target.value)} data-testid="ig-bulk-cat" className="rounded-lg border border-white/15 bg-black/40 px-2.5 py-1.5 text-xs text-white outline-none">
                  <option value="">Add to category…</option>
                  {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <button onClick={applyBulk} disabled={!bulkCat || bulkBusy} data-testid="ig-bulk-apply" className="flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">{bulkBusy ? <Loader2 size={12} className="animate-spin" /> : <Tags size={12} />} Apply</button>
                <button onClick={() => setSelected([])} className="text-xs font-semibold text-[#A1A1AA] hover:text-white">Clear</button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="ig-admin-template-grid">
              {templates.map((t) => (
                <div key={t.id} data-testid={`ig-admin-template-${t.id}`} className={`relative overflow-hidden rounded-xl border bg-white/[0.02] ${selected.includes(t.id) ? "border-[#E1306C]" : "border-white/10"}`}>
                  <label className="absolute left-2 top-2 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-md bg-black/60 backdrop-blur">
                    <input type="checkbox" checked={selected.includes(t.id)} onChange={() => toggleSelect(t.id)} data-testid={`ig-bulk-check-${t.id}`} className="accent-[#E1306C]" />
                  </label>
                  <div className="aspect-square bg-black"><img src={abs(t.thumbnail_url || t.image_url)} alt={t.name} className="h-full w-full object-cover" /></div>
                  <div className="p-2">
                    <div className="truncate text-xs font-bold text-white">{t.name}</div>
                    <div className="text-[10px] text-[#666]">{t.recommended_aspect} · {new Date(t.created_at).toLocaleDateString()}</div>
                    {!!(t.categories || (t.category ? [t.category] : [])).length && (
                      <div className="mt-1 flex flex-wrap gap-1" data-testid={`ig-tpl-cats-${t.id}`}>
                        {(t.categories || [t.category]).filter(Boolean).map((cn) => <span key={cn} className="rounded-full bg-[#E1306C]/15 px-1.5 py-0.5 text-[9px] font-semibold text-[#f48fb1]">{cn}</span>)}
                      </div>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-1">
                      <button onClick={() => patchTpl(t.id, { enabled: !t.enabled })} data-testid={`ig-toggle-template-${t.id}`}
                        className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${t.enabled ? "bg-[#22c55e]/15 text-[#22c55e]" : "bg-white/10 text-[#A1A1AA]"}`}>{t.enabled ? "Enabled" : "Disabled"}</button>
                      <button onClick={() => openManageCats(t)} title="Manage Categories" data-testid={`ig-manage-cats-${t.id}`} className="rounded p-1 text-[#A1A1AA] hover:text-[#f48fb1]"><Tags size={12} /></button>
                      <button onClick={() => rename(t)} title="Rename" className="rounded p-1 text-[#A1A1AA] hover:text-white"><Pencil size={12} /></button>
                      <button onClick={() => { setReplaceId(t.id); replaceRef.current?.click(); }} title="Replace image" className="rounded p-1 text-[#A1A1AA] hover:text-white"><Upload size={12} /></button>
                      <button onClick={() => duplicate(t)} title="Duplicate" className="rounded p-1 text-[#A1A1AA] hover:text-white"><Copy size={12} /></button>
                      <button onClick={() => del(t)} title="Delete" className="rounded p-1 text-[#A1A1AA] hover:text-red-400"><Trash2 size={12} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            </>
          )}
        </div>
      </div>

      {manageCatsFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="ig-manage-cats-modal" onClick={() => setManageCatsFor(null)}>
          <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-[#111214] p-5" onClick={(e) => e.stopPropagation()}>
            <WindowSearch />
            <div className="mb-1 flex items-center justify-between">
              <h3 className="text-base font-bold text-white">Manage Categories</h3>
              <button onClick={() => setManageCatsFor(null)} className="text-white/50 hover:text-white"><X size={18} /></button>
            </div>
            <p className="mb-3 truncate text-xs text-[#A1A1AA]">{manageCatsFor.name}</p>
            <div className="max-h-72 space-y-1 overflow-auto pr-1">
              {cats.map((c) => (
                <label key={c.id} data-testid={`ig-catopt-${c.name}`} className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-white hover:bg-white/5">
                  <input type="checkbox" checked={catSel.includes(c.name)} onChange={() => toggleCatSel(c.name)} className="accent-[#E1306C]" data-testid={`ig-catchk-${c.name}`} />
                  <span className="flex-1">{c.name}</span>
                  <span className="text-[10px] text-[#666]">{c.template_count}</span>
                </label>
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setManageCatsFor(null)} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-white">Cancel</button>
              <button onClick={saveCats} disabled={savingCats} data-testid="ig-manage-cats-save" className="inline-flex items-center gap-1.5 rounded-lg bg-[#E1306C] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">{savingCats ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
